import { type ReactNode, useId } from 'react';
import classNames from 'classnames';

import { FormFieldContext, joinIds } from './FormFieldContext';

import styles from './FormField.module.scss';

export interface FormFieldProps {
    label?: ReactNode;
    /**
     * The control's id. Generated when omitted and handed to the TextField, Select,
     * TextArea, Checkbox or StyledSingleDatePicker inside; pass it when the control sets
     * its own id. Every control inside without an id takes this one, so a second control
     * in the same field needs an id of its own.
     */
    htmlFor?: string;
    /** Shown after the label in a lighter weight, e.g. "(optional)". */
    labelSuffix?: ReactNode;
    /** Inline affordance on the label row, right-aligned. */
    labelAction?: ReactNode;
    /** Guidance under the control. It describes the control but does not mark it invalid. */
    hint?: ReactNode;
    /**
     * `danger` for a hint that warns, such as "no empty cages available"; `secondary` where a
     * form's notes have always read in the secondary text colour.
     */
    hintTone?: 'muted' | 'secondary' | 'danger';
    /** A validation message. When set, the control inside is marked invalid. */
    error?: ReactNode;
    /** `sm` sets the label, hint and error at 12px, for a dense form such as a wizard step. */
    size?: 'md' | 'sm';
    /**
     * `inline` puts the label in a column beside the control from `xs` up, as in a filter
     * panel, with the hint and error under the control in 12px. Phones keep it stacked.
     */
    layout?: 'stacked' | 'inline';
    /**
     * The label's id, for a control named by `aria-labelledby` instead of the label's
     * `htmlFor`, such as a menu button. The field then hands no id to the controls inside,
     * so the checkboxes of a menu it opens keep their own names.
     */
    labelId?: string;
    className?: string;
    labelClassName?: string;
    children: ReactNode;
}

function isShown(node: ReactNode): boolean {
    return node !== undefined && node !== null && node !== false && node !== '';
}

/**
 * Label, control, hint and error for one form field. Fields are 16px apart; a form that
 * wants a tighter rhythm sets `--form-field-margin` on a container around them, and
 * `--form-field-label-margin` to space a label further from its control.
 */
export default function FormField(props: FormFieldProps) {
    const {
        label,
        htmlFor,
        labelSuffix,
        labelAction,
        hint,
        hintTone = 'muted',
        error,
        size = 'md',
        layout = 'stacked',
        labelId,
        className,
        labelClassName,
        children,
    } = props;

    // useId's colons are valid in an id but not in a CSS selector, and tests select by id.
    const generatedId = useId().replace(/:/g, '');
    const id = htmlFor ?? generatedId;
    const hasHint = isShown(hint);
    const hasError = isShown(error);
    const hintId = hasHint ? `${id}-hint` : undefined;
    const errorId = hasError ? `${id}-error` : undefined;

    // One grid cell beside the label, however many controls the field holds.
    const controls =
        layout === 'inline' ? <div className={styles.control}>{children}</div> : children;

    const labelElement = isShown(label) ? (
        <label
            id={labelId}
            className={classNames(styles.label, labelClassName)}
            htmlFor={labelId === undefined ? id : undefined}>
            {label}
            {isShown(labelSuffix) && <span className={styles.labelSuffix}>{labelSuffix}</span>}
        </label>
    ) : null;

    return (
        <div
            className={classNames(
                styles.field,
                size === 'sm' && styles.sm,
                layout === 'inline' && styles.inline,
                className
            )}>
            {isShown(labelAction) ? (
                <div className={styles.labelRow}>
                    {labelElement}
                    {labelAction}
                </div>
            ) : (
                labelElement
            )}
            {labelId === undefined ? (
                <FormFieldContext
                    value={{ id, describedBy: joinIds(errorId, hintId), invalid: hasError }}>
                    {controls}
                </FormFieldContext>
            ) : (
                controls
            )}
            {hasHint && (
                <p
                    id={hintId}
                    className={classNames(styles.hint, hintTone !== 'muted' && styles[hintTone])}>
                    {hint}
                </p>
            )}
            {/* The region is there before the message, so a screen reader reads an error out
                when validation adds it, without interrupting what it is saying. */}
            <div className={styles.errorRegion} aria-live="polite">
                {hasError && (
                    <p id={errorId} className={styles.error}>
                        {error}
                    </p>
                )}
            </div>
        </div>
    );
}
